import { useState } from 'react';
import InterestModal from './InterestModal';

interface Partner {
  id: string;
  name: string;
  nameEn: string;
  role: string;
  category: 'strategic' | 'knowledge' | 'community';
  quote: string;
  logo?: string;
  initial: string;
  color: string;
  bgColor: string;
  website?: string;
}

const partners: Partner[] = [
  {
    id: 'sbm-foundation',
    name: 'مؤسسة سالم بن محفوظ الأهلية',
    nameEn: 'Salem Bin Mahfouz Foundation',
    role: 'شريك استراتيجي معتمد',
    category: 'strategic',
    quote:
      'نؤمن بأن الاستثمار في شباب اليوم هو بناء لمجتمع الغد، ونفخر بدعم مبادرات تُرسّخ قيم الابتكار والريادة.',
    logo: '/sbm-foundation.png',
    initial: 'س',
    color: '#B8860B',
    bgColor: '#FFFBEB',
    website: 'https://sbmf.org.sa',
  },
];

const categoryLabels: Record<Partner['category'], { label: string; icon: string; color: string }> =
  {
    strategic: {
      label: 'شريك استراتيجي',
      icon: 'ri-shield-star-line',
      color: 'text-blue-700 bg-blue-50 border-blue-200',
    },
    knowledge: {
      label: 'شريك معرفي',
      icon: 'ri-book-open-line',
      color: 'text-purple-700 bg-purple-50 border-purple-200',
    },
    community: {
      label: 'شريك مجتمعي',
      icon: 'ri-team-line',
      color: 'text-green-700 bg-green-50 border-green-200',
    },
  };

const trustStats = [
  {
    svg: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-[#FF6B6B]">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
    value: 'موثّق',
    label: 'شراكة رسمية موثّقة',
  },
  {
    svg: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-[#FF6B6B]">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ),
    value: '100%',
    label: 'التزام بالشفافية',
  },
  {
    svg: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-[#FF6B6B]">
        <circle cx="12" cy="8" r="6"/>
        <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
      </svg>
    ),
    value: 'معتمد',
    label: 'مدعوم من منظمات وطنية',
  },
];

export default function Partners() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section id="partners" className="py-20 bg-gradient-to-b from-white to-[#FFF5F3]">
      <div className="container mx-auto px-6 lg:px-12" dir="rtl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-5 py-2 bg-[#FF6B6B]/10 text-[#FF6B6B] rounded-full text-sm font-semibold mb-6 border border-[#FF6B6B]/20">
            <i className="ri-handshake-line"></i>
            شركاء يمنحون المشروع ثقة ومصداقية
          </span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4"
            style={{ lineHeight: '1.8' }}
          >
            شركاء يؤمنون{' '}
            <span className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] bg-clip-text text-transparent">
              بالشباب
            </span>{' '}
            ورهان على المستقبل
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            مساحة شباب لا تقوم وحدها — بل بمنظومة شركاء استراتيجيين وماليين ومعرفيين يؤمنون بأن الاستثمار في الشباب هو أذكى استثمار.
          </p>
        </div>

        {/* Partner Cards */}
        <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-16">
          {partners.map((partner) => {
            const cat = categoryLabels[partner.category];
            return (
              <div
                key={partner.id}
                className="relative bg-white rounded-3xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                {/* Top accent bar */}
                <div
                  className="h-1.5 w-full"
                  style={{
                    background: `linear-gradient(to left, #FF6B6B, ${partner.color})`,
                  }}
                />

                <div className="p-6">
                  {/* Category badge */}
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border mb-5 ${cat.color}`}
                  >
                    <i className={cat.icon}></i>
                    {cat.label}
                  </span>

                  {/* Logo — full width */}
                  <div
                    className="w-full h-28 rounded-2xl flex items-center justify-center overflow-hidden mb-5 border border-gray-100 transition-transform duration-300 group-hover:scale-[1.02]"
                    style={{ backgroundColor: partner.bgColor }}
                  >
                    {partner.logo ? (
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="w-full h-full object-contain px-6 py-3"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `<span style="font-size:2.5rem;font-weight:700;color:${partner.color}">${partner.initial}</span>`;
                        }}
                      />
                    ) : (
                      <span className="text-4xl font-bold" style={{ color: partner.color }}>
                        {partner.initial}
                      </span>
                    )}
                  </div>

                  {/* Names */}
                  <div className="text-center mb-5">
                    <h3 className="text-lg font-bold text-gray-900 leading-snug">
                      {partner.name}
                    </h3>
                    {partner.nameEn && (
                      <p className="text-xs text-[#B8860B] font-semibold mt-1 tracking-wide">
                        {partner.nameEn}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{partner.role}</p>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-5" />

                  {/* Quote */}
                  <blockquote className="relative">
                    <i className="ri-double-quotes-r text-3xl text-[#FF6B6B]/20 absolute -top-2 right-0"></i>
                    <p className="text-gray-500 text-sm leading-relaxed pr-4 italic">
                      {partner.quote}
                    </p>
                  </blockquote>

                  {/* Role badge */}
                  <div className="mt-5 flex items-center gap-2 text-xs font-semibold text-[#B8860B]">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    شريك استراتيجي معتمد
                  </div>
                </div>
              </div>
            );
          })}

          {/* Placeholder: Financial Partner */}
          <div
            className="relative bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-2 border-dashed border-emerald-200 hover:border-emerald-400 transition-all duration-300 flex flex-col items-center justify-center p-8 text-center cursor-pointer group min-h-[260px]"
            onClick={() => setIsModalOpen(true)}
          >
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform duration-300">
              <img src="/saudi-riyal.svg" alt="ريال سعودي" className="w-8 h-8 brightness-0 invert" />
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full border border-emerald-200 mb-3">
              موقع شاغر
            </span>
            <h3 className="text-lg font-bold text-gray-800 mb-2">شريك مالي</h3>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              داعم مالي يُسهم في بناء البنية التحتية وتمويل البرامج
            </p>
            <span className="mt-4 inline-flex items-center gap-2 text-emerald-600 font-semibold text-sm group-hover:gap-3 transition-all">
              تواصل معنا
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
              </svg>
            </span>
          </div>

          {/* Placeholder: Knowledge Partner */}
          <div
            className="relative bg-gradient-to-br from-violet-50 to-purple-50 rounded-3xl border-2 border-dashed border-violet-200 hover:border-violet-400 transition-all duration-300 flex flex-col items-center justify-center p-8 text-center cursor-pointer group min-h-[260px]"
            onClick={() => setIsModalOpen(true)}
          >
            <div className="w-16 h-16 bg-gradient-to-br from-violet-400 to-purple-500 rounded-2xl flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform duration-300">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 bg-violet-100 px-3 py-1 rounded-full border border-violet-200 mb-3">
              موقع شاغر
            </span>
            <h3 className="text-lg font-bold text-gray-800 mb-2">شريك معرفي</h3>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              جامعة أو مؤسسة أكاديمية تُثري البرامج بالمحتوى والكفاءات
            </p>
            <span className="mt-4 inline-flex items-center gap-2 text-violet-600 font-semibold text-sm group-hover:gap-3 transition-all">
              تواصل معنا
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
              </svg>
            </span>
          </div>
        </div>

        {/* Trust Stats Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-8 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 divide-y sm:divide-y-0 sm:divide-x sm:divide-x-reverse divide-gray-100">
            {trustStats.map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center pt-6 sm:pt-0 first:pt-0">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B]/10 to-[#FF8E53]/10 rounded-xl flex items-center justify-center mb-3">
                  {stat.svg}
                </div>
                <span className="text-2xl font-extrabold text-gray-900 mb-1">{stat.value}</span>
                <span className="text-sm text-gray-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>


      </div>

      <InterestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        projectName="مساحة شباب - شريك جديد"
      />
    </section>
  );
}
